/* Instantiate native account controls only when their original location is visible. */
(() => {
  'use strict';
  if (window.aceAccountLoaderStarted) return;
  window.aceAccountLoaderStarted = true;

  const selector = '[data-ace-account-slot]';
  const pending = new Set();
  const waiting = new WeakSet();
  const watched = new WeakSet();
  let observer;

  const visible = (slot) => {
    if (!slot.isConnected || !slot.getClientRects().length) return false;
    if (slot.closest('dialog:not([open]),[hidden],[inert]')) return false;
    if (typeof slot.checkVisibility === 'function' &&
        !slot.checkVisibility({ checkVisibilityCSS: true })) return false;
    const rect = slot.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < document.documentElement.clientHeight &&
      rect.left < document.documentElement.clientWidth;
  };

  const activate = (slot) => {
    if (!pending.has(slot) || waiting.has(slot) || !visible(slot)) return;
    waiting.add(slot);
    // Preserve the real account link if Shopify's component is unavailable.
    const ready = customElements.get('shopify-account')
      ? Promise.resolve()
      : customElements.whenDefined('shopify-account');
    ready.then(() => {
      waiting.delete(slot);
      if (!pending.has(slot) || !visible(slot)) return;
      const template = slot.querySelector('template[data-ace-account-template]');
      const account = template?.content.querySelector('shopify-account');
      if (!account) return;
      const nativeAccount = account.cloneNode(true);
      slot.replaceChildren(nativeAccount);
      pending.delete(slot);
      observer?.unobserve(slot);
      slot.setAttribute('data-ace-account-ready', '');
    }).catch(() => waiting.delete(slot));
  };

  const scan = (root) => {
    const slots = [];
    if (root.matches?.(selector)) slots.push(root);
    slots.push(...root.querySelectorAll(selector));
    for (const slot of slots) {
      if (slot.hasAttribute('data-ace-account-ready') || watched.has(slot)) continue;
      watched.add(slot);
      pending.add(slot);
      if (observer) observer.observe(slot);
      else activate(slot);
    }
  };

  const refreshVisible = () => {
    for (const slot of pending) {
      if (!slot.isConnected) {
        observer?.unobserve(slot);
        pending.delete(slot);
        watched.delete(slot);
      } else activate(slot);
    }
  };

  const start = () => {
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        for (const entry of entries) if (entry.isIntersecting) activate(entry.target);
      }, { rootMargin: '0px', threshold: 0 });
    }
    scan(document);
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        }
      }
      for (const slot of pending) {
        if (!slot.isConnected) {
          observer?.unobserve(slot);
          pending.delete(slot);
          watched.delete(slot);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    // Geometry changes wake responsive controls, while native dialog opening
    // also handles the no-IntersectionObserver fallback.
    window.addEventListener('resize', refreshVisible, { passive: true });
    document.addEventListener('toggle', refreshVisible, true);
    if (!observer) window.addEventListener('scroll', refreshVisible, { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
